import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// แก้ไขปัญหา Icon ของ Leaflet ใน React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Component สำหรับจัดการการคลิกบนแผนที่
const LocationMarker = ({ position, setPosition }) => {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  return position === null ? null : (
    <Marker position={position} />
  );
};

const CarParkForm = ({ userProfile }) => {
  const [userId, setUserId] = useState(userProfile?.userId || '');
  const [floor, setFloor] = useState('');
  const [position, setPosition] = useState(null); // เก็บค่า { lat, lng }
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (userProfile?.userId) {
      setUserId(userProfile.userId);
    }
    
    // ลองดึงตำแหน่งปัจจุบันมาเป็นจุดเริ่มต้นของแผนที่
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      }, (err) => {
        console.log("Cannot get current location", err);
      });
    }
  }, [userProfile]);

  const handleSearch = async () => {
    if (!searchQuery) return;
    
    setIsSearching(true);
    try {
      // ใช้ Nominatim API ของ OpenStreetMap (ฟรี)
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`);
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error("Error searching location:", error);
      alert("Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchResult = (lat, lon) => {
    setPosition({ lat: parseFloat(lat), lng: parseFloat(lon) });
    setSearchResults([]); // ซ่อนผลลัพธ์หลังเลือก
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!position) {
      alert('กรุณาปักหมุดตำแหน่งบนแผนที่ (Please pin a location)');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('parking_logs') // เปลี่ยนไปใช้ Table ใหม่
        .insert([{ 
          user_id: userId, // ใช้ user_id (snake_case) ให้ตรงกับ DB
          latitude: position.lat,
          longitude: position.lng,
          floor: floor,
          source: 'app'
        }]);

      if (error) throw error;
      alert('Location saved successfully');
      // Reset form (optional)
    } catch (error) {
      console.error('Error saving location:', error.message);
      alert('Error saving location: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: '#fff', padding: '30px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <h2 style={{ color: 'var(--color-primary)', textAlign: 'center', marginBottom: '30px', fontSize: '24px' }}>🚗 Record Parking</h2>
        
        <form onSubmit={handleSubmit}>
          {/* User Info Section (View Only) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>Driver (LINE Account)</label>
            <div style={{ padding: '12px', backgroundColor: 'var(--color-surface-muted)', borderRadius: '8px', color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid var(--color-border)' }}>
              {userProfile?.pictureUrl ? (
                <img src={userProfile.pictureUrl} alt="Profile" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
              ) : (
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--color-border)' }}></div>
              )}
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{userProfile?.displayName || 'Guest'}</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>ID: {userId ? `${userId.substring(0, 8)}...` : '-'}</div>
              </div>
            </div>
          </div>

          {/* Floor Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>Floor / Zone</label>
            <input 
              type="text" 
              value={floor} 
              onChange={(e) => setFloor(e.target.value)} 
              placeholder="e.g. 5A, B2"
              required 
              style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '16px', boxSizing: 'border-box', outlineColor: 'var(--color-primary)' }}
            />
          </div>

          {/* Map Section */}
          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--color-text-muted)', fontWeight: '500' }}>Pin Location</label>
            
            {/* Search Box */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
                placeholder="Search place (e.g. Central World)"
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '14px' }}
              />
              <button 
                type="button" 
                onClick={handleSearch}
                disabled={isSearching}
                style={{ padding: '10px 15px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                {isSearching ? '...' : 'Search'}
              </button>
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px 0', border: '1px solid var(--color-border)', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto', backgroundColor: '#fff' }}>
                {searchResults.map((result) => (
                  <li 
                    key={result.place_id}
                    onClick={() => selectSearchResult(result.lat, result.lon)}
                    style={{ padding: '10px', borderBottom: '1px solid var(--color-surface-muted)', cursor: 'pointer', fontSize: '14px', color: 'var(--color-text)' }}
                  >
                    {result.display_name}
                  </li>
                ))}
              </ul>
            )}

            <div style={{ height: '300px', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--color-border)', position: 'relative', zIndex: 0 }}>
              <MapContainer center={[13.7563, 100.5018]} zoom={13} style={{ height: '100%', width: '100%' }}>
                <TileLayer 
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
                />
                <LocationMarker position={position} setPosition={setPosition} />
              </MapContainer>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--color-text-subtle)', marginTop: '8px', textAlign: 'center' }}>
              {position ? `Selected: ${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}` : 'Tap on the map to pin your location'}
            </p>
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={loading} 
            style={{ 
              width: '100%', 
              padding: '16px', 
              backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              fontSize: '16px', 
              fontWeight: 'bold', 
              cursor: 'pointer', 
              boxShadow: '0 4px 10px rgba(var(--color-primary-rgb), 0.2)',
              transition: 'all 0.2s'
            }}
          >
            {loading ? 'Saving...' : 'Save Parking Location'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CarParkForm;
